import React, { useState, useEffect, useReducer, useRef } from "react";
import { useHistory } from "react-router-dom";

import { isSameDay, parseISO, format } from "date-fns";
import clsx from "clsx";

import { green } from "@material-ui/core/colors";
import {
  Button,
  CircularProgress,
  Divider,
  IconButton,
  makeStyles,
} from "@material-ui/core";

import {
  AccessTime,
  Block,
  Done,
  DoneAll,
  ExpandMore,
  GetApp,
  Lock,
} from "@material-ui/icons";

import MarkdownWrapper from "../MarkdownWrapper";
import ModalImageCors from "../ModalImageCors";
import MessageOptionsMenu from "../MessageOptionsMenu";
import LocationPreview from "../LocationPreview";
import VcardPreview from "../VcardPreview";
import NewTicketModal from "../NewTicketModal";
import ScheduleModal from "../ScheduleModal";


import api from "../../services/api";
import toastError from "../../errors/toastError";
import { socketConnection } from "../../services/socket";
import { parseVcard, parseVcardsArray } from "../../utils/parseVcard";
import { MESSAGE_LOCAL_SYNC_EVENT } from "../../utils/ticketUtils";

// Tipos que já têm uma pré-visualização rica própria — o texto cru do body
// (coordenadas, vCard) não deve ser exibido junto, só o preview.
const RICH_PREVIEW_ONLY_TYPES = [
  "locationMessage",
  "contactMessage",
  "contactsArrayMessage",
];

const useStyles = makeStyles((theme) => ({
  "@keyframes messagePop": {
    "0%": {
      opacity: 0,
      transform: "scale(0.55) translateY(22px)",
    },
    "45%": {
      opacity: 1,
      transform: "scale(1.06) translateY(-2px)",
    },
    "70%": {
      transform: "scale(0.985) translateY(0)",
    },
    "100%": {
      opacity: 1,
      transform: "scale(1) translateY(0)",
    },
  },

  lastMessageIn: {
    animation: "$messagePop 360ms cubic-bezier(0.34, 1.56, 0.64, 1)",
    transformOrigin: "bottom left",
  },

  lastMessageOut: {
    animation: "$messagePop 360ms cubic-bezier(0.34, 1.56, 0.64, 1)",
    transformOrigin: "bottom right",
  },

  messagesListWrapper: {
    overflow: "hidden",
    position: "relative",
    display: "flex",
    flexDirection: "column",
    flexGrow: 1,
    width: "100%",
    minWidth: 300,
    minHeight: 200,
  },

  messagesList: {
    // Fundo liso nos tons da marca (antes: papel de parede do WhatsApp)
    backgroundColor: theme.palette.type === "dark" ? "#141E30" : "#EEF2F7",
    display: "flex",
    flexDirection: "column",
    flexGrow: 1,
    padding: "20px 20px 20px 20px",
    overflowY: "auto",
    ...theme.scrollbarStyles,
  },

  circleLoading: {
    color: green[500],
    position: "absolute",
    opacity: "70%",
    top: 0,
    left: "50%",
    marginTop: 12,
  },

  messageLeft: {
    marginRight: 20,
    marginTop: 2,
    minWidth: 100,
    maxWidth: 600,
    height: "auto",
    display: "block",
    position: "relative",
    "&:hover #messageActionsButton": {
      display: "flex",
      position: "absolute",
      top: 0,
      right: 0,
    },
    whiteSpace: "pre-wrap",
    backgroundColor: theme.palette.type === "dark" ? "#1C2A40" : "#ffffff",
    color: theme.palette.type === "dark" ? "#E7ECF4" : "#0F1E36",
    alignSelf: "flex-start",
    borderTopLeftRadius: 4,
    borderTopRightRadius: 12,
    borderBottomLeftRadius: 12,
    borderBottomRightRadius: 12,
    paddingLeft: 6,
    paddingRight: 6,
    paddingTop: 5,
    paddingBottom: 0,
    boxShadow: theme.palette.type === "dark" ? "0 1px 2px rgba(0,0,0,0.35)" : "0 1px 2px rgba(15,30,54,0.08)",
  },

  quotedContainerLeft: {
    margin: "-3px -80px 6px -6px",
    overflow: "hidden",
    backgroundColor: theme.palette.type === "dark" ? "#15233A" : "#F0F3F8",
    borderRadius: 8,
    display: "flex",
    position: "relative",
  },

  quotedMsg: {
    padding: 10,
    maxWidth: 300,
    height: "auto",
    display: "block",
    whiteSpace: "pre-wrap",
    overflow: "hidden",
  },

  quotedSideColorLeft: {
    flex: "none",
    width: "4px",
    backgroundColor: "#F08A3C",
  },

  messageRight: {
    marginLeft: 20,
    marginTop: 2,
    minWidth: 100,
    maxWidth: 600,
    height: "auto",
    display: "block",
    position: "relative",
    "&:hover #messageActionsButton": {
      display: "flex",
      position: "absolute",
      top: 0,
      right: 0,
    },
    whiteSpace: "pre-wrap",
    backgroundColor: theme.palette.type === "dark" ? "#1E4479" : "#DCEBFF",
    color: theme.palette.type === "dark" ? "#EEF4FF" : "#0F1E36",
    alignSelf: "flex-end",
    borderTopLeftRadius: 12,
    borderTopRightRadius: 4,
    borderBottomLeftRadius: 12,
    borderBottomRightRadius: 12,
    paddingLeft: 6,
    paddingRight: 6,
    paddingTop: 5,
    paddingBottom: 0,
    boxShadow: theme.palette.type === "dark" ? "0 1px 2px rgba(0,0,0,0.35)" : "0 1px 2px rgba(15,30,54,0.08)",
  },

  quotedContainerRight: {
    margin: "-3px -80px 6px -6px",
    overflowY: "hidden",
    backgroundColor: theme.palette.type === "dark" ? "#173866" : "#C9DDFA",
    borderRadius: 8,
    display: "flex",
    position: "relative",
  },

  quotedMsgRight: {
    padding: 10,
    maxWidth: 300,
    height: "auto",
    whiteSpace: "pre-wrap",
  },

  quotedSideColorRight: {
    flex: "none",
    width: "4px",
    backgroundColor: "#2D7BE8",
  },

  messageActionsButton: {
    display: "none",
    position: "relative",
    color: theme.palette.text.secondary,
    zIndex: 1,
    backgroundColor: "inherit",
    opacity: "90%",
    "&:hover, &.Mui-focusVisible": { backgroundColor: "inherit" },
  },

  messageContactName: {
    display: "flex",
    color: theme.palette.type === "dark" ? "#8DB9F5" : "#2D7BE8",
    fontWeight: 500,
  },

  textContentItem: {
    overflowWrap: "break-word",
    padding: "3px 80px 6px 6px",
  },

  textContentItemDeleted: {
    fontStyle: "italic",
    color: theme.palette.type === "dark" ? "rgba(255, 255, 255, 0.36)" : "rgba(0, 0, 0, 0.36)",
    overflowWrap: "break-word",
    padding: "3px 80px 6px 6px",
  },

  messageMedia: {
    objectFit: "cover",
    width: 250,
    height: 200,
    borderTopLeftRadius: 8,
    borderTopRightRadius: 8,
    borderBottomLeftRadius: 8,
    borderBottomRightRadius: 8,
  },

  timestamp: {
    fontSize: 11,
    position: "absolute",
    bottom: 0,
    right: 5,
    color: theme.palette.type === "dark" ? "#9FB0C8" : "#6B7689",
  },

  // Separador de dia: pílula discreta
  dailyTimestamp: {
    alignItems: "center",
    textAlign: "center",
    alignSelf: "center",
    backgroundColor: theme.palette.type === "dark" ? "#1A283E" : "#E3E8F0",
    margin: "10px",
    borderRadius: 999,
  },

  dailyTimestampText: {
    color: theme.palette.type === "dark" ? "#9FB0C8" : "#6B7689",
    padding: "3px 12px",
    fontSize: "0.75rem",
    alignSelf: "center",
    marginLeft: "0px",
  },

  ackIcons: {
    fontSize: 18,
    verticalAlign: "middle",
    marginLeft: 4,
  },

  deletedIcon: {
    fontSize: 18,
    verticalAlign: "middle",
    marginRight: 4,
  },

  // Lida: azul da marca
  ackDoneAllIcon: {
    color: theme.palette.type === "dark" ? "#8DB9F5" : "#2D7BE8",
    fontSize: 18,
    verticalAlign: "middle",
    marginLeft: 4,
  },

  downloadMedia: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "inherit",
    padding: 10,
  },

  downloadButton: {
    color: theme.palette.primary.main,
    borderColor: theme.palette.primary.main,
  },

  messageNote: {
    marginLeft: 60,
    marginRight: 20,
    marginTop: 2,
    minWidth: 100,
    maxWidth: 560,
    height: "auto",
    display: "block",
    position: "relative",
    "&:hover #messageActionsButton": {
      display: "flex",
      position: "absolute",
      top: 0,
      right: 0,
    },
    whiteSpace: "pre-wrap",
    backgroundColor: theme.palette.type === "dark" ? "#3d2e00" : "#fff8e1",
    color: theme.palette.type === "dark" ? "#ffe082" : "#5d4037",
    alignSelf: "flex-end",
    borderTopLeftRadius: 12,
    borderTopRightRadius: 4,
    borderBottomLeftRadius: 12,
    borderBottomRightRadius: 12,
    paddingLeft: 6,
    paddingRight: 6,
    paddingTop: 5,
    paddingBottom: 0,
    boxShadow: "none",
    border: `1px dashed ${theme.palette.type === "dark" ? "#7c5500" : "#ffc107"}`,
  },

  messageNoteLabel: {
    display: "flex",
    alignItems: "center",
    gap: 4,
    fontSize: "0.68rem",
    fontWeight: 600,
    color: theme.palette.type === "dark" ? "#ffc107" : "#e65100",
    padding: "0 6px 2px 6px",
    opacity: 0.85,
  },
}));

const reducer = (state, action) => {
  if (action.type === "LOAD_MESSAGES") {
    const messages = action.payload;
    const newMessages = [];

    messages.forEach((message) => {
      const messageIndex = state.findIndex((m) => m.id === message.id);
      if (messageIndex !== -1) {
        state[messageIndex] = message;
      } else {
        newMessages.push(message);
      }
    });

    return [...newMessages, ...state];
  }

  if (action.type === "ADD_MESSAGE") {
    const newMessage = action.payload;
    const messageIndex = state.findIndex((m) => m.id === newMessage.id);

    if (messageIndex !== -1) {
      state[messageIndex] = newMessage;
    } else {
      state.push(newMessage);
    }

    return [...state];
  }

  if (action.type === "UPDATE_MESSAGE") {
    const messageToUpdate = action.payload;
    const messageIndex = state.findIndex((m) => m.id === messageToUpdate.id);

    if (messageIndex !== -1) {
      state[messageIndex] = messageToUpdate;
    }

    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }

  // Mescla a página mais recente para recuperar mensagens que o socket não
  // entregou (conexão momentaneamente fora no instante do envio/recebimento).
  // Diferente de LOAD_MESSAGES (usado ao rolar para cima e buscar páginas
  // MAIS ANTIGAS, sempre inseridas no início), aqui a mensagem nova de fato
  // pertence ao FIM da lista — inserir no início inverteria a ordem.
  if (action.type === "SYNC_LATEST") {
    const messages = action.payload;
    const newMessages = [];

    messages.forEach((message) => {
      const messageIndex = state.findIndex((m) => m.id === message.id);
      if (messageIndex !== -1) {
        state[messageIndex] = message;
      } else {
        newMessages.push(message);
      }
    });

    return [...state, ...newMessages];
  }
};

const MessagesList = ({ ticketId, isGroup }) => {
  const classes = useStyles();
  const history = useHistory();

  const [messagesList, dispatch] = useReducer(reducer, []);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const lastMessageRef = useRef();

  const [selectedMessage, setSelectedMessage] = useState({});
  const [anchorEl, setAnchorEl] = useState(null);
  const messageOptionsMenuOpen = Boolean(anchorEl);
  const currentTicketId = useRef(ticketId);

  // Modais compartilhados para as ações do card de contato compartilhado
  // (VcardPreview): uma única instância de cada, aberta com o contato clicado,
  // em vez de um modal por mensagem de contato na conversa.
  const [ticketModalOpen, setTicketModalOpen] = useState(false);
  // NewTicketModal fica sempre montado nesta tela (mesmo fechado, o Dialog
  // do MUI ainda avalia os filhos) e seu renderContactAutocomplete() acessa
  // initialContact.id sem proteger contra null — só contra undefined. Por
  // isso o valor inicial precisa ser {} (mesma convenção já usada em
  // pages/Contacts), nunca null.
  const [ticketModalContact, setTicketModalContact] = useState({});
  const [scheduleModalOpen, setScheduleModalOpen] = useState(false);
  const [scheduleModalContact, setScheduleModalContact] = useState(null);

  const handleStartConversation = (contact) => {
    setTicketModalContact(contact);
    setTicketModalOpen(true);
  };

  const handleCloseTicketModal = (ticket) => {
    setTicketModalOpen(false);
    setTicketModalContact({});
    if (ticket?.uuid) {
      history.push(`/tickets/${ticket.uuid}`);
    }
  };

  const handleScheduleMessage = (contact) => {
    setScheduleModalContact(contact);
    setScheduleModalOpen(true);
  };

  const handleCloseScheduleModal = () => {
    setScheduleModalOpen(false);
    setScheduleModalContact(null);
  };

  // Busca a página mais recente e mescla via SYNC_LATEST (sem mexer em
  // pageNumber/scroll). Usado como rede de segurança quando o socket pode
  // ter perdido o evento de uma mensagem nova — mesma classe de problema já
  // corrigida nas listas de tickets (conexão momentaneamente fora ao voltar
  // do segundo plano, ou reconectando no instante do envio/recebimento).
  const syncLatestMessages = async () => {
    if (ticketId === undefined) return;
    try {
      const { data } = await api.get("/messages/" + ticketId, {
        params: { pageNumber: 1 },
      });
      if (currentTicketId.current === ticketId) {
        dispatch({ type: "SYNC_LATEST", payload: data.messages });
      }
    } catch (err) {
      toastError(err);
    }
  };

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);

    currentTicketId.current = ticketId;
  }, [ticketId]);

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      const fetchMessages = async () => {
        if (ticketId === undefined) return;
        try {
          const { data } = await api.get("/messages/" + ticketId, {
            params: { pageNumber },
          });

          if (currentTicketId.current === ticketId) {
            dispatch({ type: "LOAD_MESSAGES", payload: data.messages });
            setHasMore(data.hasMore);
            setLoading(false);
          }

          if (pageNumber === 1 && data.messages.length > 1) {
            scrollToBottom();
          }
        } catch (err) {
          setLoading(false);
          toastError(err);
        }
      };
      fetchMessages();
    }, 500);
    return () => {
      clearTimeout(delayDebounceFn);
    };
  }, [pageNumber, ticketId]);

  useEffect(() => {
    const companyId = localStorage.getItem("companyId");
    const socket = socketConnection({ companyId });

    // Se a sessão NÃO foi recuperada pelo connectionStateRecovery do
    // servidor (quedas >2min, ou reconexão limpa), mensagens enviadas
    // durante a queda nunca chegam via socket — sincroniza a página mais
    // recente para recuperá-las.
    let hasConnectedBefore = false;
    socket.on("connect", () => {
      socket.emit("joinChatBox", `${ticketId}`);
      if (hasConnectedBefore && !socket.recovered) {
        syncLatestMessages();
      }
      hasConnectedBefore = true;
    });

    socket.on(`company-${companyId}-appMessage`, (data) => {
      // Ignora eventos de outros tickets (a conexão escuta a empresa toda)
      if (data.message && String(data.message.ticketId) !== String(ticketId)) {
        return;
      }

      if (data.action === "create") {
        dispatch({ type: "ADD_MESSAGE", payload: data.message });
        scrollToBottom();
      }

      if (data.action === "update") {
        dispatch({ type: "UPDATE_MESSAGE", payload: data.message });
      }
    });

    return () => {
      socket.disconnect();
    };
    // Depende apenas de ticketId para manter a conexão estável enquanto o
    // ticket estiver aberto. Antes dependia do objeto `ticket`, que muda de
    // referência a cada update e derrubava/recriava o socket, fazendo perder
    // o evento da mensagem recém-enviada (só aparecia após F5).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticketId]);

  // Ao voltar do segundo plano, a conexão pode ter morrido sem o cliente
  // perceber ainda (janela "zumbi" antes do ping detectar a queda) —
  // sincroniza a página mais recente para não deixar mensagens paradas na
  // tela sem que um F5 seja necessário.
  const hiddenSinceRef = useRef(null);
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        hiddenSinceRef.current = Date.now();
        return;
      }
      if (hiddenSinceRef.current && Date.now() - hiddenSinceRef.current >= 5000) {
        syncLatestMessages();
      }
      hiddenSinceRef.current = null;
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticketId]);

  // Rede de segurança logo após um envio (ver emitLocalMessageSync): o envio
  // ao WhatsApp e a persistência/emissão do evento de socket são etapas
  // assíncronas separadas no backend, então o POST pode responder OK antes
  // do evento "create" chegar — ou, mais raramente, ele simplesmente se
  // perder. O atraso dá tempo da persistência no backend terminar; se o
  // socket já tiver entregue a mensagem normalmente, o merge é um no-op.
  useEffect(() => {
    const handleLocalMessageSync = (e) => {
      if (String(e.detail) !== String(ticketId)) return;
      setTimeout(syncLatestMessages, 1500);
    };
    window.addEventListener(MESSAGE_LOCAL_SYNC_EVENT, handleLocalMessageSync);
    return () => {
      window.removeEventListener(MESSAGE_LOCAL_SYNC_EVENT, handleLocalMessageSync);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticketId]);

  const loadMore = () => {
    setPageNumber((prevPageNumber) => prevPageNumber + 1);
  };

  const scrollToBottom = () => {
    if (lastMessageRef.current) {
      lastMessageRef.current.scrollIntoView({});
    }
  };

  const handleScroll = (e) => {
    if (!hasMore) return;
    const { scrollTop } = e.currentTarget;

    if (scrollTop === 0) {
      document.getElementById("messagesList").scrollTop = 1;
    }

    if (loading) {
      return;
    }

    if (scrollTop < 50) {
      loadMore();
    }
  };

  const handleOpenMessageOptionsMenu = (e, message) => {
    setAnchorEl(e.currentTarget);
    setSelectedMessage(message);
  };

  const handleCloseMessageOptionsMenu = (e) => {
    setAnchorEl(null);
  };

  const checkMessageMedia = (message) => {
    if (message.mediaType === "locationMessage" && message.body.split('|').length >= 2) {
      let locationParts = message.body.split('|')
      let imageLocation = locationParts[0]
      let linkLocation = locationParts[1]

      let descriptionLocation = null

      if (locationParts.length > 2)
        descriptionLocation = message.body.split('|')[2]

      return <LocationPreview image={imageLocation} link={linkLocation} description={descriptionLocation} />
    }
    else if (message.mediaType === "contactMessage") {
      const contact = parseVcard(message.body);
      if (!contact) return null;
      return (
        <VcardPreview
          name={contact.name}
          numbers={contact.numbers}
          onStartConversation={handleStartConversation}
          onScheduleMessage={handleScheduleMessage}
        />
      );
    }
    else if (message.mediaType === "contactsArrayMessage") {
      const contacts = parseVcardsArray(message.body);
      if (contacts.length === 0) return null;
      return (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {contacts.map((c) => (
            <VcardPreview
              key={c.numbers[0]?.raw || c.name}
              name={c.name}
              numbers={c.numbers}
              onStartConversation={handleStartConversation}
              onScheduleMessage={handleScheduleMessage}
            />
          ))}
        </div>
      );
    }
    else if (message.mediaType === "image") {
      return <ModalImageCors imageUrl={message.mediaUrl} />;
    } else if (message.mediaType === "audio") {
      return (
        <audio controls>
          <source src={message.mediaUrl} type="audio/ogg"></source>
        </audio>
      );
    } else if (message.mediaType === "video") {
      return (
        <video
          className={classes.messageMedia}
          src={message.mediaUrl}
          controls
        />
      );
    } else {
      return (
        <>
          <div className={classes.downloadMedia}>
            <Button
              startIcon={<GetApp />}
              variant="outlined"
              target="_blank"
              href={message.mediaUrl}
              className={classes.downloadButton}
            >
              Download
            </Button>
          </div>
          <Divider />
        </>
      );
    }
  };

  const renderMessageAck = (message) => {
    if (message.ack === 1) {
      return <AccessTime fontSize="small" className={classes.ackIcons} />;
    }
    if (message.ack === 2) {
      return <Done fontSize="small" className={classes.ackIcons} />;
    }
    if (message.ack === 3) {
      return <DoneAll fontSize="small" className={classes.ackIcons} />;
    }
    if (message.ack === 4 || message.ack === 5) {
      return <DoneAll fontSize="small" className={classes.ackDoneAllIcon} />;
    }
  };

  const renderDailyTimestamps = (message, index) => {
    if (index === 0) {
      return (
        <span
          className={classes.dailyTimestamp}
          key={`timestamp-${message.id}`}
        >
          <div className={classes.dailyTimestampText}>
            {format(parseISO(messagesList[index].createdAt), "dd/MM/yyyy")}
          </div>
        </span>
      );
    }
    if (index < messagesList.length - 1) {
      let messageDay = parseISO(messagesList[index].createdAt);
      let previousMessageDay = parseISO(messagesList[index - 1].createdAt);

      if (!isSameDay(messageDay, previousMessageDay)) {
        return (
          <span
            className={classes.dailyTimestamp}
            key={`timestamp-${message.id}`}
          >
            <div className={classes.dailyTimestampText}>
              {format(parseISO(messagesList[index].createdAt), "dd/MM/yyyy")}
            </div>
          </span>
        );
      }
    }
    if (index === messagesList.length - 1) {
      return (
        <div
          key={`ref-${message.createdAt}`}
          ref={lastMessageRef}
          style={{ float: "left", clear: "both" }}
        />
      );
    }
  };

  const renderNumberTicket = (message, index) => {
    if (index < messagesList.length && index > 0) {

      let messageTicket = message.ticketId;
      let connectionName = message.ticket?.whatsapp?.name;
      let previousMessageTicket = messagesList[index - 1].ticketId;

      if (messageTicket !== previousMessageTicket) {
        return (
          <center>
            <div className={classes.ticketNunberClosed}>
              Conversa encerrada: {format(parseISO(messagesList[index - 1].createdAt), "dd/MM/yyyy HH:mm:ss")}
            </div>

            <div className={classes.ticketNunberOpen}>
              Conversa iniciada: {format(parseISO(message.createdAt), "dd/MM/yyyy HH:mm:ss")}
            </div>
          </center>
        );
      }
    }
  };

  const renderMessageDivider = (message, index) => {
    if (index < messagesList.length && index > 0) {
      let messageUser = messagesList[index].fromMe;
      let previousMessageUser = messagesList[index - 1].fromMe;

      if (messageUser !== previousMessageUser) {
        return (
          <span style={{ marginTop: 16 }} key={`divider-${message.id}`}></span>
        );
      }
    }
  };

  const renderQuotedMessage = (message) => {
    return (
      <div
        className={clsx(classes.quotedContainerLeft, {
          [classes.quotedContainerRight]: message.fromMe,
        })}
      >
        <span
          className={clsx(classes.quotedSideColorLeft, {
            [classes.quotedSideColorRight]: message.quotedMsg?.fromMe,
          })}
        ></span>
        <div className={classes.quotedMsg}>
          {!message.quotedMsg?.fromMe && (
            <span className={classes.messageContactName}>
              {message.quotedMsg?.contact?.name}
            </span>
          )}

          {message.quotedMsg.mediaType === "audio"
            && (
              <div className={classes.downloadMedia}>
                <audio controls>
                  <source src={message.quotedMsg.mediaUrl} type="audio/ogg"></source>
                </audio>
              </div>
            )
          }
          {message.quotedMsg.mediaType === "video"
            && (
              <video
                className={classes.messageMedia}
                src={message.quotedMsg.mediaUrl}
                controls
              />
            )
          }
          {message.quotedMsg.mediaType === "application"
            && (
              <div className={classes.downloadMedia}>
                <Button
                  startIcon={<GetApp />}
                  color="primary"
                  variant="outlined"
                  target="_blank"
                  href={message.quotedMsg.mediaUrl}
                >
                  Download
                </Button>
              </div>
            )
          }

          {message.quotedMsg.mediaType === "image"
            && (
              <ModalImageCors imageUrl={message.quotedMsg.mediaUrl} />)
            || message.quotedMsg?.body}

        </div>
      </div>
    );
  };

  const renderMessages = () => {
    if (messagesList.length > 0) {
      const viewMessagesList = messagesList.map((message, index) => {
        const isLastMessage = index === messagesList.length - 1;

        if (message.mediaType === "call_log") {
          return (
            <React.Fragment key={message.id}>
              {renderDailyTimestamps(message, index)}
              {renderNumberTicket(message, index)}
              {renderMessageDivider(message, index)}
              <div className={classes.messageCenter}>
                <IconButton
                  variant="contained"
                  size="small"
                  id="messageActionsButton"
                  disabled={message.isDeleted}
                  className={classes.messageActionsButton}
                  onClick={(e) => handleOpenMessageOptionsMenu(e, message)}
                >
                  <ExpandMore />
                </IconButton>
                {isGroup && (
                  <span className={classes.messageContactName}>
                    {message.contact?.name}
                  </span>
                )}
                <div>
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 17" width="20" height="17">
                    <path fill="#df3333" d="M18.2 12.1c-1.5-1.8-5-2.7-8.2-2.7s-6.7 1-8.2 2.7c-.7.8-.3 2.3.2 2.8.2.2.3.3.5.3 1.4 0 3.6-.7 3.6-.7.5-.2.8-.5.8-1v-1.3c.7-1.2 5.4-1.2 6.4-.1l.1.1v1.3c0 .2.1.4.2.6.1.2.3.3.5.4 0 0 2.2.7 3.6.7.2 0 1.4-2 .5-3.1zM5.4 3.2l4.7 4.6 5.8-5.7-.9-.8L10.1 6 6.4 2.3h2.5V1H4.1v4.8h1.3V3.2z"></path>
                  </svg> <span>Chamada de voz/vídeo perdida às {format(parseISO(message.createdAt), "HH:mm")}</span>
                </div>
              </div>
            </React.Fragment>
          );
        }

        if (message.isPrivate) {
          return (
            <React.Fragment key={message.id}>
              {renderDailyTimestamps(message, index)}
              {renderNumberTicket(message, index)}
              <div className={classes.messageNote}>
                <IconButton
                  variant="contained"
                  size="small"
                  id="messageActionsButton"
                  className={classes.messageActionsButton}
                  onClick={(e) => handleOpenMessageOptionsMenu(e, message)}
                >
                  <ExpandMore />
                </IconButton>
                <span className={classes.messageNoteLabel}>
                  <Lock style={{ fontSize: 11 }} />
                  Anotação interna
                </span>
                <div className={classes.textContentItem}>
                  <MarkdownWrapper>{message.body}</MarkdownWrapper>
                  <span className={classes.timestamp}>
                    {format(parseISO(message.createdAt), "HH:mm")}
                  </span>
                </div>
              </div>
            </React.Fragment>
          );
        }

        if (!message.fromMe) {
          return (
            <React.Fragment key={message.id}>
              {renderDailyTimestamps(message, index)}
              {renderNumberTicket(message, index)}
              {renderMessageDivider(message, index)}
              <div
                className={clsx(classes.messageLeft, {
                  [classes.lastMessageIn]: isLastMessage,
                })}
              >
                <IconButton
                  variant="contained"
                  size="small"
                  id="messageActionsButton"
                  disabled={message.isDeleted}
                  className={classes.messageActionsButton}
                  onClick={(e) => handleOpenMessageOptionsMenu(e, message)}
                >
                  <ExpandMore />
                </IconButton>
                {isGroup && (
                  <span className={classes.messageContactName}>
                    {message.contact?.name}
                  </span>
                )}

                {/* aviso de mensagem apagado pelo contato */}
                {message.isDeleted && (
                  <div>
                    <span className={"message-deleted"}
                    >Essa mensagem foi apagada pelo contato &nbsp;
                      <Block
                        color="error"
                        fontSize="small"
                        className={classes.deletedIcon}
                      />
                    </span>
                  </div>
                )}

                {(message.mediaUrl || RICH_PREVIEW_ONLY_TYPES.includes(message.mediaType)
                ) && checkMessageMedia(message)}
                <div className={classes.textContentItem}>
                  {message.quotedMsg && renderQuotedMessage(message)}
                  <MarkdownWrapper>{RICH_PREVIEW_ONLY_TYPES.includes(message.mediaType) ? null : message.body}</MarkdownWrapper>
                  <span className={classes.timestamp}>
                    {format(parseISO(message.createdAt), "HH:mm")}
                  </span>
                </div>
              </div>
            </React.Fragment>
          );
        } else {
          return (
            <React.Fragment key={message.id}>
              {renderDailyTimestamps(message, index)}
              {renderNumberTicket(message, index)}
              {renderMessageDivider(message, index)}
              <div
                className={clsx(classes.messageRight, {
                  [classes.lastMessageOut]: isLastMessage,
                })}
              >
                <IconButton
                  variant="contained"
                  size="small"
                  id="messageActionsButton"
                  disabled={message.isDeleted}
                  className={classes.messageActionsButton}
                  onClick={(e) => handleOpenMessageOptionsMenu(e, message)}
                >
                  <ExpandMore />
                </IconButton>
                {(message.mediaUrl || RICH_PREVIEW_ONLY_TYPES.includes(message.mediaType)
                ) && checkMessageMedia(message)}
                <div
                  className={clsx(classes.textContentItem, {
                    [classes.textContentItemDeleted]: message.isDeleted,
                  })}
                >
                  {message.isDeleted && (
                    <Block
                      color="disabled"
                      fontSize="small"
                      className={classes.deletedIcon}
                    />
                  )}
                  {message.quotedMsg && renderQuotedMessage(message)}
                  <MarkdownWrapper>{RICH_PREVIEW_ONLY_TYPES.includes(message.mediaType) ? null : message.body}</MarkdownWrapper>
                  <span className={classes.timestamp}>
                    {format(parseISO(message.createdAt), "HH:mm")}
                    {renderMessageAck(message)}
                  </span>
                </div>
              </div>
            </React.Fragment>
          );
        }
      });
      return viewMessagesList;
    } else {
      return <div>Diga olá para seu novo contato!</div>;
    }
  };

  return (
    <div className={classes.messagesListWrapper}>
      <MessageOptionsMenu
        message={selectedMessage}
        anchorEl={anchorEl}
        menuOpen={messageOptionsMenuOpen}
        handleClose={handleCloseMessageOptionsMenu}
      />

      {/* Ações do card de contato compartilhado (VcardPreview) */}
      <NewTicketModal
        modalOpen={ticketModalOpen}
        initialContact={ticketModalContact}
        onClose={handleCloseTicketModal}
      />
      <ScheduleModal
        open={scheduleModalOpen}
        onClose={handleCloseScheduleModal}
        contactId={scheduleModalContact?.id}
      />
      <div
        id="messagesList"
        className={classes.messagesList}
        onScroll={handleScroll}
      >
        {messagesList.length > 0 ? renderMessages() : []}
      </div>
      {loading && (
        <div>
          <CircularProgress className={classes.circleLoading} />
        </div>
      )}
    </div>
  );
};

export default MessagesList;
